import { Interface, Block, Page, OutlineBlock } from "@zapier/canvas-state";
import { Icon } from "@zapier/design-system";
import ListItems from "../ListItems";
import styles from "../StepDetails.module.css";
import { OpenAssetButton } from "../OpenAssetButton";
import { publishedProjectUrl } from "../../../utils/getPublishedInterfaceUrl";

const getIcon = (type: string) => {
  if (type === "text" || type === "textarea") return "formText";
  if (type === "email") return "actionEmail";
  if (type === "date-picker") return "actionCalendar";

  return "formTextAlignCenter";
};

interface PageBlocksProps {
  blocks: Block[] | OutlineBlock[];
}

const richBlockTypes = ["form-block", "kanban-block"];

const blockTypeNames: { [key: string]: string } = {
  "divider-block": "Divider",
  "field-block": "Field",
  "form-block": "Form",
  "kanban-block": "Kanban",
  "links-block": "Links",
  "links-card-block": "Links Card",
  "markdown-text-block": "Markdown Text",
  "media-block": "Media",
  "table-listing-block": "Table Listing",
  "embed-block": "Embed",
  "button-block": "Button",
  "checklist-block": "Checklist",
  "chatbot-block": "Chatbot",
  "layout-block": "Layout",
};

function PageBlocks({ blocks }: PageBlocksProps) {
  if (blocks.find((block) => richBlockTypes.includes(block.type))) {
    return blocks.map((block) => {
      const blockId = block?.config?.id || "block-id";

      if (block.type === "form-block") {
        return (
          <div key={blockId} className={styles.section}>
            <div className={styles.sectionLabel}>Form fields</div>
            <ListItems
              items={block.children.map(({ config }) => {
                return {
                  icon: <Icon name={getIcon(config.inputType)} size={14} />,
                  text: config.label,
                };
              })}
            />
          </div>
        );
      }

      if (block.type === "kanban-block") {
        return (
          <div key={blockId} className={styles.section}>
            <div className={styles.sectionLabel}>Kanban groups</div>
            {block?.config?.selectedColumns &&
            block.config.selectedColumns.length > 0 ? (
              <ListItems
                items={block.config.selectedColumns.map((column) => ({
                  text: column.label,
                  icon: <Icon name="miscStepsAlt" size={14} />,
                }))}
              />
            ) : (
              <ListItems
                items={[
                  {
                    text: block?.config?.title || "Kanban Board",
                    icon: <Icon name="miscStepsAlt" size={14} />,
                  },
                ]}
              />
            )}
          </div>
        );
      }
    });
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>
        Components
        <div className={styles.count}>{blocks.length}</div>
      </div>
      {blocks.length === 0 ? (
        <ListItems
          items={[{}].map(() => ({
            icon: <Icon name="fileGuide" size={14} />,
            text: "Empty",
          }))}
        />
      ) : (
        <ListItems
          items={blocks.map((block) => ({
            icon: <Icon name="fileGuide" size={14} />,
            text: blockTypeNames[block.type] || block.type,
          }))}
        />
      )}
    </div>
  );
}

interface PageDetailsProps {
  page: Page;
  project: Interface;
  proposalId?: string;
}

export function PageDetails({ page, project, proposalId }: PageDetailsProps) {
  if (!page?.blocks) return null;

  return (
    <div className={styles.wrapper}>
      <PageBlocks blocks={page.blocks} />
      {!proposalId && (
        <OpenAssetButton
          href={`${publishedProjectUrl(project, page)}`}
          eventAsset="Interface Page"
          assetNoun="page"
        />
      )}
    </div>
  );
}
